// components/sections/Pricing.js
import React from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import Container from '../common/Container';
import { SectionTitle, SectionSubtitle } from '../common/SharedStyles';
import Button from '../common/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';


const PricingWrapper = styled.section`
  display: flex;
  align-items: center;
  min-height: 90vh;
  padding: 6rem 0;

  @media (max-width: 768px) {
    padding: 5rem 0;
  }
`;

const PricingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  margin-top: 3rem;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    max-width: 500px;
    margin: 3rem auto 0;
  }
`;

const PricingCard = styled.div`
  background: var(--card-background);
  border-radius: 12px;
  padding: 2rem;
  box-shadow: var(--card-shadow);
  transition: all 0.3s ease;
  position: relative;
  width: 100%;
  box-sizing: border-box;

  h3 {
    color: var(--text-primary);
  }

  @media (max-width: 576px) {
    padding: 1.5rem;
  }

  ${props => props.popular && `
    transform: scale(1.05);
    border: 2px solid var(--accent-primary);

    @media (max-width: 968px) {
      transform: none;
    }
  `}

  ${props => props.comingSoon && `
    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(255, 255, 255, 0.7);
      backdrop-filter: blur(4px);
      border-radius: 12px;
      z-index: 1;
    }
  `}

  &:hover {
    transform: translateY(-5px);
    box-shadow: var(--card-shadow-hover);
  }
`;

const PriceTag = styled.div`
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 1rem 0;

  .currency {
    font-size: 1.5rem;
    vertical-align: super;
  }

  .period {
    font-size: 1rem;
    color: var(--text-secondary);
    font-weight: normal;
  }

  .original-price {
    font-size: 1.5rem;
    color: var(--text-secondary);
    text-decoration: line-through;
    margin-left: 0.5rem;
  }

  .price-note {
    display: block;
    font-size: 0.9rem;
    color: var(--text-secondary);
    font-weight: normal;
    margin-top: 0.5rem;
  }
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 2rem 0;

  li {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    margin-bottom: 0.75rem;
    color: var(--text-secondary);
    word-break: break-word;

    svg {
      color: var(--accent-primary);
      flex-shrink: 0;
      margin-top: 0.25rem;
    }
  }

  @media (max-width: 576px) {
    margin: 1.5rem 0;

    li {
      font-size: 0.95rem;
    }
  }
`;

const PopularBadge = styled.div`
  background: var(--accent-primary);
  color: white;
  padding: 0.25rem 1rem;
  border-radius: 20px;
  display: inline-block;
  margin-bottom: 1rem;
`;

const ComingSoonBadge = styled.div`
  background: #888;
  color: white;
  padding: 0.25rem 1rem;
  border-radius: 20px;
  display: inline-block;
  margin-bottom: 1rem;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 2;
  font-size: 1.5rem;
  font-weight: bold;
`;

const Pricing = () => {
    const navigate = useNavigate();

    const handleGetStarted = (plan) => {
        navigate('/get-started?mode=register');
    };

    const plans = [
        {
            id: 0,
            name: "Phase 1",
            price: 0,
            regularPrice: 200,
            isPromo: true,
            features: [
                "Find the best priced materials from top suppliers",
                "Create professional quotes for clients",
                "Calculate expenses, hourly rates, and profit margins",
                "Track material costs for tax purposes",
                "Monthly email showing how much you saved using Sivo",
                "24/7 customer support",
                "2 days free, then $200/month unless cancelled"
            ]
        },
        {
            id: 1,
            name: "Phase 2",
            price: 99,
            regularPrice: 99,
            comingSoon: true,
            features: [
                "All Phase 1 features",
                "Advanced reporting",
                "Inventory management",
                "Client management system",
                "Custom templates",
                "API access"
            ]
        },
        {
            id: 2,
            name: "Phase 3",
            price: 149,
            regularPrice: 149,
            comingSoon: true,
            features: [
                "All Phase 2 features",
                "Enterprise-level analytics",
                "White-label solution",
                "Custom integrations",
                "Dedicated account manager",
                "Priority support",
                "Custom development"
            ]
        }
    ];

    return (
        <PricingWrapper id="pricing">
            <Container>
                <SectionTitle>Simple, Transparent Pricing</SectionTitle>
                <SectionSubtitle>
                    Choose the plan that best fits your business needs
                </SectionSubtitle>

                <PricingGrid>
                    {plans.map((plan, index) => (
                        <PricingCard key={index} popular={plan.popular} comingSoon={plan.comingSoon}>
                            {plan.comingSoon && <ComingSoonBadge>Coming Soon</ComingSoonBadge>}
                            <h3>{plan.name}</h3>
                            <PriceTag>
                                {index === 0 ? (
                                    <>
                                        FREE
                                        <span className="price-note">2 days free, then $200/month unless cancelled</span>
                                    </>
                                ) : (
                                    <>
                                        <span className="currency">$</span>
                                        {plan.price}
                                        <span className="period">/mo</span>
                                    </>
                                )}
                            </PriceTag>
                            <FeatureList>
                                {plan.features.map((feature, idx) => (
                                    <li key={idx}>
                                        <FontAwesomeIcon icon={faCheck} />
                                        {feature}
                                    </li>
                                ))}
                            </FeatureList>
                            <Button
                                variant={index === 0 ? "primary" : "secondary"}
                                fullWidth
                                disabled={plan.comingSoon}
                                onClick={!plan.comingSoon ? () => handleGetStarted(plan) : undefined}
                            >
                                {plan.comingSoon ? "Coming Soon" : "Get Started"}
                            </Button>
                        </PricingCard>
                    ))}
                </PricingGrid>
            </Container>
        </PricingWrapper>
    );
};

export default Pricing;
